import { useState, useEffect } from "react";
import { Search, UserPlus, MoreVertical, MapPin, CheckCircle, XCircle, Edit } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { getWorkers, verifyWorker } from "@/services/api";

export const Workers = () => {
  const [workers, setWorkers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWorkers = async () => {
      try {
        const data = await getWorkers();
        if (data && data.success) {
          setWorkers(data.workers);
        }
      } catch (error) {
        console.error("Error fetching workers:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchWorkers();
  }, []);

  const filteredWorkers = workers.filter(worker => {
    const name = worker.name || "";
    const code = worker.id || worker.workerId || worker.code || ""; // Seeding mapped code to workerId
    const area = worker.assignedRoute || worker.area || ""; // Seeding mapped area to assignedRoute
    const contact = worker.contact || "";
    
    const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         code.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         area.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contact.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || worker.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) return <div>Loading...</div>;

  const handleToggleStatus = (workerId) => {
    setWorkers(workers.map(worker => 
      worker.id === workerId 
        ? { ...worker, status: worker.status === "active" ? "off-duty" : "active" }
        : worker
    ));
    toast.success("Worker status updated");
  };

  const handleToggleVerification = async (workerId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await verifyWorker(workerId, newStatus);
      setWorkers(workers.map(worker => 
        worker.id === workerId 
          ? { ...worker, isVerified: newStatus }
          : worker
      ));
      toast.success(newStatus ? "Worker verified" : "Worker unverified");
    } catch (error) {
      console.error("Error updating verification:", error);
      toast.error("Failed to update verification status");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "bg-success text-success-foreground";
      case "off-duty": return "bg-warning text-warning-foreground";
      case "inactive": return "bg-muted text-muted-foreground";
      default: return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Captain Management</h1>
          <p className="text-muted-foreground mt-1">Manage captains, assign routes, and track performance</p>
        </div>
        <Button className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-md">
          <UserPlus className="w-4 h-4 mr-2" />
          Add Captain
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Captains</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">{workers.length}</h3>
              </div>
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Captains</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">
                  {workers.filter(w => w.status === "active").length}
                </h3>
              </div>
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Jobs</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">
                  {workers.reduce((sum, w) => sum + w.activeJobs, 0)}
                </h3>
              </div>
              <MapPin className="w-8 h-8 text-secondary" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Completed Today</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">0</h3>
              </div>
              <CheckCircle className="w-8 h-8 text-accent" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Workers List */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle>Captains List</CardTitle>
          <CardDescription>View and manage all captains</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, code or area..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="off-duty">Off Duty</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Captain</TableHead>
                  <TableHead className="hidden md:table-cell">Area</TableHead>
                  <TableHead>Verification</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="hidden lg:table-cell">Active Jobs</TableHead>
                  <TableHead className="hidden lg:table-cell">Completed</TableHead>
                  <TableHead className="hidden xl:table-cell">Rating</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredWorkers.map((worker) => (
                  <TableRow key={worker.id} className="hover:bg-muted/50 transition-smooth">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary text-primary-foreground">
                            {worker.name ? worker.name.split(' ').map(n => n[0]).join('') : 'W'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-foreground">{worker.name}</p>
                          <p className="text-sm text-muted-foreground">{worker.contact}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">{worker.area}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={worker.isVerified ? "default" : "outline"}
                        className={`cursor-pointer ${worker.isVerified ? "bg-success text-success-foreground" : "text-muted-foreground"}`}
                        onClick={() => handleToggleVerification(worker.id, worker.isVerified)}
                      >
                        {worker.isVerified ? "Verified" : "Unverified"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`capitalize ${getStatusColor(worker.status)}`}>
                        {(worker.status || "inactive").replace('-', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="font-medium">{worker.activeJobs}</span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="font-medium">{worker.completedJobs}</span>
                    </TableCell>
                    <TableCell className="hidden xl:table-cell">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{worker.rating}</span>
                        <span className="text-warning">★</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Captain
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <MapPin className="w-4 h-4 mr-2" />
                            Assign Route
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleStatus(worker.id)}>
                            {worker.status === "active" ? (
                              <><XCircle className="w-4 h-4 mr-2" />Mark Off Duty</>
                            ) : (
                              <><CheckCircle className="w-4 h-4 mr-2" />Mark Active</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleVerification(worker.id, worker.isVerified)}>
                            {worker.isVerified ? (
                              <><XCircle className="w-4 h-4 mr-2" />Unverify Captain</>
                            ) : (
                              <><CheckCircle className="w-4 h-4 mr-2" />Verify Captain</>
                            )}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Workers;
